import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { mapFieldErrors } from '@/api/fieldErrors';
import type { AffiliateSettingsView, AffiliateSettingsWriteRequest } from '@/api/generated';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { ApiErrorState } from '@/components/ApiErrorState';
import { SubmitButton } from '@/components/SubmitButton';
import { useActionError, type ActionError } from '@/components/useActionError';
import { useFieldErrors } from '@/components/useFieldErrors';

export interface AffiliateSettingsDialogProps {
  open: boolean;
  settings: AffiliateSettingsView | null | undefined;
  /** While settings have not loaded, the dialog shows this instead of the form. */
  settingsError?: unknown;
  onRetrySettings?: () => void;
  settingsLoading?: boolean;
  onClose: () => void;
  onSave: (body: AffiliateSettingsWriteRequest) => Promise<void>;
}

const FIELDS = ['attributionWindowDays'] as const;

export function AffiliateSettingsDialog({
  open,
  settings,
  settingsError = null,
  onRetrySettings,
  settingsLoading = false,
  onClose,
  onSave,
}: AffiliateSettingsDialogProps) {
  const { t } = useTranslation();
  const describe = useActionError();
  const windowRef = useRef<HTMLInputElement>(null);
  const [days, setDays] = useState('30');
  const [selfReferral, setSelfReferral] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ActionError | null>(null);
  const fields = useFieldErrors(FIELDS, { attributionWindowDays: windowRef }, submitting);
  const { reset } = fields;

  useEffect(() => {
    if (!open) return;
    setDays(String(settings?.attributionWindowDays ?? 30));
    setSelfReferral(settings?.selfReferralAllowed ?? false);
    setError(null);
    reset();
  }, [open, settings, reset]);

  function handleClose() {
    if (submitting) return;
    onClose();
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const windowDays = Number(days);
    if (days.trim() === '' || !Number.isInteger(windowDays) || windowDays < 1 || windowDays > 365) {
      setError(null);
      fields.fail({ attributionWindowDays: t('validation.attributionWindow') });
      return;
    }
    setSubmitting(true);
    setError(null);
    fields.reset();
    try {
      await onSave({ attributionWindowDays: windowDays, selfReferralAllowed: selfReferral });
      onClose();
    } catch (err) {
      const action = describe(err);
      setError(action);
      fields.failMarked(
        mapFieldErrors(action.fieldKeys, { attributionWindowDays: 'attributionWindowDays' }),
        { attributionWindowDays: t('affiliates.field.window') },
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : handleClose} fullWidth maxWidth="sm">
      <Box component="form" noValidate onSubmit={(e) => void handleSubmit(e)}>
        <DialogTitle>{t('affiliates.settingsTitle')}</DialogTitle>
        <DialogContent>
          {settingsError != null && onRetrySettings ? (
            <ApiErrorState error={settingsError} onRetry={onRetrySettings} />
          ) : settingsLoading ? (
            <Alert severity="info">{t('common.loading')}</Alert>
          ) : (
            <>
              <ActionErrorAlert error={error} sx={{ mb: 2 }} />
              <TextField
                inputRef={windowRef}
                required
                fullWidth
                type="number"
                margin="dense"
                label={t('affiliates.settingsWindow')}
                helperText={fields.errors.attributionWindowDays ?? t('affiliates.windowHelp')}
                error={fields.errors.attributionWindowDays !== undefined}
                value={days}
                onChange={(e) => {
                  setDays(e.target.value);
                  fields.clear('attributionWindowDays');
                }}
                slotProps={{ htmlInput: { min: 1, max: 365, step: 1 } }}
                disabled={submitting}
              />
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Switch
                    checked={selfReferral}
                    onChange={(e) => setSelfReferral(e.target.checked)}
                    disabled={submitting}
                  />
                }
                label={t('affiliates.selfReferral')}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          {/* Disabled only while the settings it edits aren't loaded, and the dialog says so. */}
          <SubmitButton
            type="submit"
            loading={submitting}
            disabled={settingsError != null || settingsLoading}
          >
            {t('affiliates.settingsSave')}
          </SubmitButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
